import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import API_BASE_URL from "./config/api";
import Navbar from "./components/Navbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLocationDot,
  faClock,
  faUsers,
  faHeart,
  faCheck,
  faChevronDown,
  faChevronUp,
  faEnvelope,
  faPhone,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import Footer from "./components/Footer";
import FloatingAIButton from "./components/FloatingAIButton";
import hotel from "./assests/hotel.png";
import Modal from "./Modal";

function Hotel() {
  const location = useLocation();
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]); // State to hold the fetched hotel data
  const [filteredHotels, setFilteredHotels] = useState([]); // State for filtered hotels
  const [isModalOpen, setModalOpen] = useState(false);
  const [favoriteHotels, setFavoriteHotels] = useState({});
  const [price, setPrice] = useState(100000);
  const [rating, setRating] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [packageDetails, setPackageDetails] = useState(null);
  const [expandUserRating, setExpandUserRating] = useState(true);
  const [expandPrice, setExpandPrice] = useState(true);
  const itineraryDetails = location.state?.itineraryDetails || null;
  const dayIndex = location.state?.dayIndex ?? null;

  // Ensure that the destination is extracted correctly and is a string
  const destination =
    location.state?.itineraryDetails?.destinations[0]?.name || "Munnar"; // Ensure it's a string and check the destination object structure

  // Fetch hotel data based on the destination
  useEffect(() => {
    const requestOptions = {
      method: "GET",
      redirect: "follow",
    };

    setLoading(true); // Start loading before the fetch request

    // Encode the destination to make sure it's safe for the URL
    fetch(
      `${API_BASE_URL}/hotels?destination=${encodeURIComponent(
        destination
      )}`,
      requestOptions
    )
      .then((response) => response.json())
      .then((result) => {
        setHotels(result); // Update state with the fetched hotel data
        setLoading(false); // Stop loading after the data is fetched
      })
      .catch((error) => {
        console.error("Error fetching hotels:", error);
        setLoading(false); // Stop loading in case of error
      });
  }, [destination]); // Now it only runs when destination changes

  // Filter hotels based on user input (price, rating, search term)
  useEffect(() => {
    const filtered = hotels.filter((hotel) => {
      const isWithinPrice = hotel.price <= price;
      const isWithinRating = hotel.rating <= rating;
      const matchesSearch = hotel.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      return isWithinPrice && isWithinRating && matchesSearch;
    });
    setFilteredHotels(filtered);
  }, [hotels, price, rating, searchTerm]);

  const handlePriceChange = (e) => setPrice(Number(e.target.value));
  const handleRatingChange = (value) => setRating(value);
  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  // Handle toggling favorite hotel
  const toggleFavorite = (id) => {
    setFavoriteHotels((prevFavorites) => ({
      ...prevFavorites,
      [id]: !prevFavorites[id],
    }));
  };

  const handleHotelClick = (pkg) => {
    navigate(`/hoteldetails`, {
      state: { hotel: pkg, hotelId: pkg._id, itineraryDetails, dayIndex },
    });
  };

  // Handle hotel selection and package update
  const handleSelectHotel = async (hotelId) => {
    if (packageDetails) {
      const updatedPackage = {
        ...packageDetails,
        itinerary: packageDetails.itinerary.map((day) => ({
          ...day,
          hotels: [
            {
              hotelId: { _id: hotelId },
              nights: 1, // You can adjust nights as needed
            },
          ],
        })),
      };

      try {
        setUpdating(true);

        const response = await fetch(
          `${API_BASE_URL}/packages/${packageDetails._id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedPackage),
          }
        );

        if (response.ok) {
          const result = await response.json();
          setPackageDetails(result);
          alert("Package updated successfully!");
          navigate(`/tripdetails?id=${packageDetails._id}`);
        } else {
          console.error("Error updating package:", await response.text());
          alert("Failed to update the package.");
        }
      } catch (error) {
        console.error("Error updating package:", error);
        alert("An error occurred while updating the package.");
      } finally {
        setUpdating(false);
      }
    }
  };

  if (loading) {
    return <div style={styles.container}>Loading hotels...</div>;
  }

  return (
    <>
      <Navbar />
      <div style={styles.destinationPage}>
        <header style={styles.destinationHeader}>
          <div style={styles.leftContent}>
            <h1 style={styles.h1}>Hotels</h1>
          </div>
          <div>
            <img
              src={hotel}
              alt="hotel image"
              style={{
                width: 1350,
                height: 300,
                objectFit: "cover",
                objectPosition: "center",
                position: "relative",
              }}
            />
          </div>
        </header>
        <div
          style={{
            color: "#004E61",
            fontFamily: "Merriweather",
            fontSize: "45px",
            fontWeight: "700",
            lineHeight: "50px",
            textAlign: "center",
          }}
        >
          Hotels For You
        </div>
      </div>
      <div style={styles.container}>
        <aside style={styles.sidebar}>
          <p
            style={{
              fontFamily: "Merriweather",
              fontSize: "25px",
              fontWeight: "700",
              color: "#02495B",
              lineHeight: "28px",
              textAlign: "justify",
            }}
          >
            Criteria
          </p>

          <hr style={styles.horizontalLine} />

          {/* Price Filter */}
          <div
            style={{
              marginBottom: "20px",
              padding: "15px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                color: "#02495B",
                fontWeight: "bold",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              Price{" "}
              <FontAwesomeIcon
                icon={faChevronDown}
                onClick={() => setExpandPrice(!expandPrice)}
                style={{ fontSize: "16px" }}
              />
            </h3>
            {expandPrice && (
              <div style={{ marginTop: "10px" }}>
                <input
                  type="range"
                  min="1000"
                  max="100000"
                  value={price}
                  onChange={handlePriceChange}
                  style={{
                    width: "100%",
                    margin: "10px 0",
                    appearance: "none",
                    background: "#ddd",
                    height: "6px",
                    borderRadius: "5px",
                    outline: "none",
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "14px",
                    color: "#555",
                  }}
                >
                  <span>Min: Rs 1,000</span>
                  <span>Price: Rs {price}</span>
                  <span>Max: Rs 100,000</span>
                </div>
              </div>
            )}
          </div>

          <hr style={{ border: "1px solid #eee", margin: "20px 0" }} />

          <div
            style={{
              marginBottom: "20px",
              padding: "15px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                color: "#02495B",
                fontWeight: "bold",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              Rating{" "}
              <FontAwesomeIcon
                icon={faChevronDown}
                onClick={() => setExpandUserRating(!expandUserRating)}
                style={{ fontSize: "16px" }}
              />
            </h3>
            {expandUserRating && (
              <div style={{ marginTop: "10px" }}>
                {[1, 2, 3, 4, 5].map((val) => (
                  <label
                    key={val}
                    style={{
                      display: "block",
                      margin: "5px 0",
                      fontSize: "14px",
                      color: "#333",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="radio"
                      name="rating"
                      checked={rating === val}
                      onChange={() => handleRatingChange(val)}
                      style={{ marginRight: "8px" }}
                    />
                    {val} Star{val > 1 ? "s" : ""}
                  </label>
                ))}
              </div>
            )}
          </div>
        </aside>
        {/* Main Content */}
        <main style={styles.mainContent}>
          {/* Search Bar */}
          <div style={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search for hotels..."
              style={styles.searchBar}
              value={searchTerm} // Bind the input value to state
              onChange={handleSearchChange} // Handle input changes
            />
            <button style={styles.searchButton}>Search</button>
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: "50px" }}>
              <h3>Loading...</h3>
            </div>
          ) : (
            <div style={styles.hotelList}>
              {/* Hotel Cards */}
              {filteredHotels.length > 0 ? (
                filteredHotels.map((pkg) => (
                  <div
                    key={pkg._id}
                    style={{
                      border: "1px solid #02495B",
                      borderRadius: "15px",
                      padding: "0px",
                      backgroundColor: "#fff",
                    }}
                  >
                    <div style={styles.hotelCard}>
                      {/* Hotel Image */}
                      <img
                        style={styles.hotelImage}
                        src={pkg.images[0]}
                        alt={pkg.name}
                      />

                      {/* Heart Icon Positioned in the Top-Right Corner */}
                      <div style={styles.heartIconContainer}>
                        <FontAwesomeIcon
                          icon={faHeart}
                          color={favoriteHotels[pkg._id] ? "red" : "grey"}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent the hotel click from triggering
                            toggleFavorite(pkg._id);
                          }}
                        />
                      </div>

                      {/* Hotel Details */}
                      <div style={styles.hotelDetails}>
                        <p
                          style={{
                            fontSize: "25px",
                            fontWeight: "700",
                            color: "#02495B",
                            fontFamily: "Merriweather",
                            lineHeight: "28px",
                            marginTop: "20px",
                          }}
                        >
                          {pkg.name}
                        </p>
                        <p
                          style={{
                            fontFamily: "Merriweather",
                            fontSize: "14px",
                            fontWeight: "400",
                            color: "#4A4A4A",
                            lineHeight: "28px",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faLocationDot}
                            style={{ marginRight: "8px", color: "#4A4A4A" }}
                          />
                          {pkg.state?.destination}
                        </p>
                        <p
                          style={{
                            fontFamily: "Merriweather",
                            fontSize: "14px",
                            fontWeight: "400",
                            color: "#4A4A4A",
                            lineHeight: "28px",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faClock}
                            style={{ marginRight: "8px", color: "#4A4A4A" }}
                          />
                          {pkg.rating} Rating
                        </p>
                        <p
                          style={{
                            fontFamily: "Merriweather",
                            fontSize: "14px",
                            fontWeight: "400",
                            color: "#4A4A4A",
                            lineHeight: "28px",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faUsers}
                            style={{ marginRight: "8px", color: "#4A4A4A" }}
                          />
                          {pkg.amenities.join(", ")}
                        </p>
                        {/* Images Gallery */}
                        <div style={styles.imageGallery}>
                          {pkg.images?.map((image, index) => (
                            <img
                              key={index}
                              src={image}
                              alt={`${pkg.name} - Image ${index + 1}`}
                              style={{
                                width: "50px",
                                height: "50px",
                                objectFit: "cover",
                                borderRadius: "8px",
                                marginRight: "10px",
                              }}
                            />
                          ))}
                        </div>
                      </div>
                      <div style={styles.verticalLine} />
                      {/* Hotel Price and Discount */}
                      <div style={styles.hotelPrice}>
                        <span
                          style={{
                            backgroundColor: "#004E61",
                            color: "white",
                            fontFamily: "Merriweather",
                            fontSize: "13px",
                            fontWeight: "300",
                            lineHeight: "28px",
                            textAlign: "justify",
                            paddingTop: "7px",
                            paddingBottom: "7px",
                            paddingInline: "12px",
                            borderRadius: "26px",
                          }}
                        >
                          {/* {pkg.discount} */}
                          15% off
                        </span>
                        <p
                          style={{
                            color: "#02495B",
                            fontFamily: "Merriweather",
                            fontSize: "23px",
                            fontWeight: "700",
                            lineHeight: "28px",
                            textAlign: "justify",
                            marginTop: 10,
                          }}
                        >
                          Rs {pkg.price} / Night
                        </p>
                      </div>
                    </div>
                    <div style={{ marginInline: "20px" }}>
                      <hr style={styles.grayLine} /> {/* Gray line above */}
                      <div style={styles.rowContainer}>
                        <div style={styles.textContainer}>
                          <p style={styles.availability}>
                            Available throughout the year
                          </p>
                          <div style={styles.months}>
                            {pkg.seasonalRates &&
                              pkg.seasonalRates.length > 0 ? (
                              pkg.seasonalRates.map((rate, index) => (
                                <div
                                  key={index}
                                  style={{ marginBottom: "10px" }}
                                >
                                  <p
                                    style={{
                                      fontFamily: "Merriweather",
                                      fontSize: "14px",
                                      fontWeight: "400",
                                      color: "#4A4A4A",
                                      lineHeight: "20px",
                                    }}
                                  >
                                    <strong>{rate.season}:</strong>{" "}
                                    {new Date(
                                      rate.startDate
                                    ).toLocaleDateString()}{" "}
                                    -{" "}
                                    {new Date(
                                      rate.endDate
                                    ).toLocaleDateString()}
                                  </p>
                                  {rate.rates && rate.rates.length > 0 && (
                                    <ul style={{ paddingLeft: "20px" }}>
                                      {rate.rates.map((room, idx) => (
                                        <li
                                          key={idx}
                                          style={{ marginBottom: "5px" }}
                                        >
                                          <span style={{ fontWeight: "600" }}>
                                            {room.roomType}:
                                          </span>{" "}
                                          Rs {room.specialRateCPAI} (CPAI), Rs{" "}
                                          {room.specialRateMAPAI} (MAPAI), Rs{" "}
                                          {room.specialRateAPAI} (APAI)
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              ))
                            ) : (
                              <p>No seasonal rates available</p>
                            )}
                          </div>
                        </div>
                        <button
                          style={styles.viewTripBtn}
                          onClick={() => handleHotelClick(pkg)}
                        >
                          View Hotel
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p>No hotels available</p>
              )}
            </div>
          )}
        </main>
      </div>
      {/* Floating box with image */}
      <div style={styles.floatingBox}>
        <FloatingAIButton onClick={openModal} />
      </div>
      {/* Modal */}
      {isModalOpen && <Modal closeModal={closeModal} />} <Footer />
    </>
  );
}

const styles = {
  destinationPage: {
    padding: "20px",
    fontFamily: "Arial, sans-serif",
  },
  destinationHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
    marginBottom: "20px",
  },
  leftContent: {
    position: "absolute",
    left: "100px",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 2,
    color: "white",
  },
  h1: {
    fontFamily: '"Merriweather", serif',
    fontSize: "70px",
    fontWeight: 400,
    marginTop: "-30px",
    marginLeft: "-40px",
    color: "#004e61",
  },
  breadcrumb: {
    fontSize: "27px",
    fontWeight: 300,
    fontFamily: '"Merriweather", serif',
    color: "#1ca8cb",
    marginTop: "5%",
  },
  breadcrumbLink: {
    textDecoration: "none",
    color: "#1ca8cb",
  },
  rightImage: {
    width: "96vw",
    height: "300px",
    objectFit: "cover",
    filter: "brightness(50%)",
    position: "relative",
  },
  container: {
    display: "flex",
    padding: "20px",
    fontFamily: "Arial, sans-serif",
  },
  sidebar: {
    width: "20%",
    padding: "20px",
    border: "1px solid #D9D9D9",
    borderRadius: "15px",
  },
  horizontalLine: {
    border: "none",
    borderTop: "1px solid #ccc",
    margin: "10px 0",
  },
  filterSection: {
    marginBottom: "20px",
  },
  city: {
    display: "block",
    marginBottom: "10px",
    fontFamily: "Merriweather",
    fontSize: "14px",
    fontWeight: "300",
    color: "#057693",
    lineHeight: "28px",
    textAlign: "justify",
  },
  expandIcon: {
    float: "right",
    cursor: "pointer",
  },
  mainContent: {
    width: "75%",
    padding: "0 20px",
  },
  searchContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "20px",
  },
  searchBar: {
    width: "88%",
    padding: "10px",
    borderRadius: "5px",
    border: "1px solid #ccc",
    fontSize: "16px",
  },
  searchButton: {
    backgroundColor: "#004E61",
    color: "#fff",
    padding: "10px 20px",
    border: "none",
    borderRadius: "30px",
    cursor: "pointer",
    fontSize: "18px",
    fontWeight: "400",
    fontFamily: "Merriweather",
  },
  subOptions: {
    marginLeft: "15px",
  },
  rangeContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    marginBottom: "20px",
  },
  rangeInput: {
    WebkitAppearance: "none", // Removes default styling
    appearance: "none", // Removes default styling
    width: "90%", // Adjust the width to fit the container
    height: "4px", // Thickness of the line
    background: "#ccc", // Line color
    borderRadius: "5px",
    outline: "none", // Removes the blue outline on focus
    opacity: 0.7, // Slight transparency
    transition: "opacity .2s",
  },
  priceValue: {
    fontFamily: "Merriweather",
    fontWeight: "700",
    margin: "5px 0", // Spacing between slider and current value
    fontSize: "12px", // Font size for the value
    color: "#004E61", // Blue color for the text
  },
  priceRange: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%", // Make it match the range slider's width
    marginTop: "5px",
    fontFamily: "Merriweather",
    fontSize: "12px",
    fontWeight: "300",
    color: "#004E61",
    lineHeight: "28px",
  },
  checkboxContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginTop: "10px",
  },
  ratingOptions: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "300px",
  },
  ratingRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "10px",
  },
  hotelList: {
    display: "grid",
    gridTemplateColumns: "1fr",
    rowGap: "20px",
  },
  hotelCard: {
    display: "flex",
    position: "relative",
  },
  hotelImage: {
    width: "393px",
    height: "303px",
    objectFit: "cover",
    borderTopLeftRadius: "15px", // Radius applied only to the top-left corner
  },
  heartIconContainer: {
    position: "absolute",
    top: "15px",
    right: "30px",
    zIndex: 1,
  },
  hotelTitle: {
    fontSize: "25px",
    fontWeight: "700",
    color: "#02495B",
    fontFamily: "Merriweather",
    lineHeight: "28px",
    margin: "0", // Removes default margin of <p> for better alignment
  },
  icon: {
    marginRight: "8px",
    color: "#4A4A4A",
  },
  hotelDetails: {
    flex: 1,
    marginLeft: "20px",
  },
  grayLine: {
    border: "none",
    borderTop: "1px solid #ccc", // Light gray color
  },
  rowContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  textContainer: {
    display: "flex",
    flexDirection: "column",
    marginTop: -10,
  },
  availability: {
    fontFamily: "Merriweather",
    fontSize: "14px",
    fontWeight: "300",
    color: "#909090",
    lineHeight: "28px",
    textAlign: "justify",
  },
  months: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    fontFamily: "Merriweather",
    fontSize: "14px",
    fontWeight: "300",
    color: "#616161",
    lineHeight: "28px",
    textAlign: "justify",
    marginTop: -10,
    marginBottom: 10,
  },
  verticalLine: {
    borderLeft: "1px solid #D9D9D9", // Light gray vertical line
    height: "227px", // Adjust height as needed
    marginRight: "20px",
    marginTop: "80px",
  },
  hotelPrice: {
    textAlign: "left",
    marginTop: 90,
    marginRight: 50,
  },
  viewTripBtn: {
    backgroundColor: "#004E61",
    color: "#fff",
    padding: "10px 20px",
    border: "none",
    borderRadius: "30px",
    cursor: "pointer",
    fontSize: "18px",
    fontWeight: "400",
    fontFamily: "Merriweather",
    marginRight: 20,
  },
  floatingBox: {
    position: "fixed",
    bottom: "20px",
    right: "115px",
    width: "96px",
    height: "96px",
    display: "flex",
    borderRadius: "15px",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
  },
  floatingButton: {
    border: "none",
    backgroundColor: "transparent",
    padding: 0,
    cursor: "pointer",
    borderRadius: "15px",
  },
  floatingImage: {
    width: "96px",
    height: "96px",
    objectFit: "cover",
    borderRadius: "15px",
  },
};
export default Hotel;
